const createProgram = ({
  name,
  location,
  type,
  sourceUrl,
  overview,
  credential,
  format,
  length,
  tuition,
  admissions,
  focus,
  highlights,
  mapEmbedUrl,
}) => ({
  name,
  location,
  type,
  credential,
  format,
  length,
  tuition,
  admissions,
  summary: overview,
  badge: type,
  websiteUrl: sourceUrl,
  sourceUrl,
  specs: [
    ['Program type', type],
    ['Credential', credential],
    ['Class format', format],
    ['Program length', length],
    ['Tuition and fees', tuition],
    ['Admissions and requirements', admissions],
  ],
  paragraphs: [
    focus,
    'Program length, tuition, prerequisites, clinical requirements, application deadlines, and cohort availability can change. Confirm all current details directly with the institution before applying.',
  ],
  highlights,
  address: location,
  mapEmbedUrl,
});

export const programs = [
  createProgram({
    name: 'Herzing University Physical Therapist Assistant Program',
    location: 'Orlando, FL',
    type: 'Private hybrid PTA degree program',
    sourceUrl: 'https://www.herzing.edu/physical-therapist-assistant',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Herzing+University+Orlando+FL&output=embed',
    overview:
      'Herzing University offers a private, hybrid Associate of Science pathway for students preparing to become physical therapist assistants in Orlando.',
    credential: 'Associate of Science in Physical Therapist Assistant',
    format:
      'Online general education courses, hands-on labs at the Orlando campus, and supervised clinical practicum',
    length: 'Approximately 20 months across 5 semesters',
    tuition:
      'Approximately $45,625 annually. The estimated technical program cost averages about $27,375 for specific institutional components. Average student loan debt upon completion is approximately $21,310.',
    admissions:
      'Applicants need a high school diploma or equivalent, must complete entrance assessments, and undergo a competitive evaluation process. Confirm current prerequisites, health documentation, background-check, and clinical requirements with Herzing University.',
    focus:
      'The fully CAPTE-accredited curriculum includes 560 hours of supervised clinical practicum experience and prepares graduates to sit for the national NPTE-PTA licensure examination.',
    highlights: [
      'Private hybrid Associate of Science program in Orlando',
      'Fully CAPTE-accredited PTA education',
      'Approximately 20 months or 5 semesters',
      '560 hours of supervised clinical practicum',
      'Approximately $45,625 annual tuition',
      'Approximately $21,310 average student loan debt upon completion',
      'Entrance assessments and competitive evaluation process',
    ],
  }),
  createProgram({
    name: 'Seminole State College Physical Therapist Assistant Program',
    location: 'Altamonte Springs Campus, Altamonte Springs, FL',
    type: 'Public limited-access PTA degree program',
    sourceUrl:
      'https://www.seminolestate.edu/health/physical-therapist-assistant',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Seminole+State+College+Altamonte+Springs+Campus+FL&output=embed',
    overview:
      'Seminole State College provides a public Associate in Science pathway for students in Altamonte Springs and the greater Orlando region.',
    credential: 'Associate in Science in Physical Therapist Assistant',
    format:
      'In-person lectures and hands-on labs at the Altamonte Springs Campus, followed by clinical rotations across Central Florida',
    length: 'Five-term lock-step sequence beginning each fall',
    tuition:
      'Core tuition and fees are approximately $3,131. Additional expenses for books, supplies, background checks, and uniforms total approximately $1,000.',
    admissions:
      'This selective, limited-access program requires prerequisite coursework such as Anatomy and Physiology, a minimum GPA, and completion of the Health Sciences application cycle. Confirm current deadlines and selection criteria directly with Seminole State College.',
    focus:
      'The fully CAPTE-accredited program combines classroom and laboratory instruction at the Altamonte Springs Campus with clinical rotations at medical facilities throughout Central Florida.',
    highlights: [
      'Public college option serving greater Orlando',
      'Fully CAPTE-accredited Associate in Science degree',
      'Five-term lock-step sequence beginning each fall',
      'Approximately $3,131 in core tuition and fees',
      'Approximately $1,000 in additional books, supplies, background-check, and uniform expenses',
      'Selective admissions with prerequisite coursework and GPA requirements',
    ],
  }),
  createProgram({
    name: 'College of Central Florida Physical Therapy Aide Training',
    location: 'Online and regional training, Ocala, FL',
    type: 'Online physical therapy aide vocational training',
    sourceUrl: 'https://www.cf.edu/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=College+of+Central+Florida+Ocala+FL&output=embed',
    overview:
      'College of Central Florida offers an online vocational pathway for students seeking entry-level physical therapy aide and rehabilitation support skills.',
    credential: 'Physical Therapy Technician/Aide Certification preparation',
    format: 'Completely self-paced, online, open-enrollment vocational training',
    length: 'Approximately 9 months and 202 course hours',
    tuition:
      'Total tuition is approximately $3,012. Confirm current pricing, course materials, payment schedule, and examination-related fees with the College of Central Florida.',
    admissions:
      'Open enrollment is available with no advanced degree prerequisites. Verify current enrollment, technology, course access, and certification eligibility requirements.',
    focus:
      'This is not a CAPTE-accredited physical therapist assistant degree. It introduces students to non-clinical rehabilitation tasks and prepares candidates to sit for the Physical Therapy Technician/Aide Certification examination through the AMCA.',
    highlights: [
      'Online and regional training option',
      'Completely self-paced learning format',
      'Approximately 9 months and 202 course hours',
      'Approximately $3,012 in tuition',
      'Open enrollment with no advanced degree prerequisite',
      'Prepares students for the AMCA PTTC examination',
    ],
  }),
  createProgram({
    name: 'ECPI University Physical Therapist Assistant Program',
    location: 'Orlando/Lake Mary Campus, Lake Mary, FL',
    type: 'Private hybrid and campus PTA degree program',
    sourceUrl:
      'https://www.ecpi.edu/programs/health-science/associate-of-applied-science-in-physical-therapist-assistant',
    mapEmbedUrl:
      'https://www.google.com/maps?q=ECPI+University+Lake+Mary+FL&output=embed',
    overview:
      'ECPI University offers a private Associate of Applied Science pathway at its Orlando/Lake Mary campus.',
    credential: 'Associate of Applied Science in Physical Therapist Assistant',
    format:
      'Hybrid and campus instruction covering foundational sciences, professional core courses, and intensive clinical education',
    length: 'As little as 18 months across 5 semesters or 75 weeks',
    tuition:
      'Private institutional tuition models apply. Confirm current tuition, fees, books, supplies, health compliance expenses, and financial aid options with ECPI University.',
    admissions:
      'Applicants must complete an online application, provide high school transcripts or a GED, and complete a financial aid consultation through FAFSA. Confirm current prerequisites, background screening, immunizations, and clinical placement requirements.',
    focus:
      'The curriculum requires 73 semester credit hours and approximately 1,725 contact hours. It includes foundational sciences, professional core courses, and intensive clinical education. The program holds CAPTE candidacy or accreditation status; confirm the current status directly with ECPI.',
    highlights: [
      'Private Orlando/Lake Mary Associate of Applied Science pathway',
      'As little as 18 months, 5 semesters, or 75 weeks',
      '73 semester credit hours',
      'Approximately 1,725 contact hours',
      'Hybrid and campus learning options',
      'CAPTE candidacy or accreditation status',
      'Online application, high school transcript or GED, and FAFSA consultation required',
    ],
  }),
  createProgram({
    name: 'ECPI University Physical Therapy Aide Program',
    location: 'Orlando/Lake Mary Campus, Lake Mary, FL',
    type: 'Private physical therapy aide vocational training',
    sourceUrl: 'https://www.ecpi.edu/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=ECPI+University+Lake+Mary+FL&output=embed',
    overview:
      'ECPI University provides a vocational training option for students interested in rapid entry into rehabilitation support environments.',
    credential: 'Physical therapy aide career training',
    format:
      'Accelerated campus or online hybrid training focused on clinical support',
    length:
      'Short-term accelerated schedule; confirm the current campus and online course calendar',
    tuition:
      'Private tuition terms apply. Confirm current tuition, supplies, payment options, and certification costs directly with ECPI University.',
    admissions:
      'Open enrollment windows may be available. Review current enrollment, scheduling, campus attendance, and technology requirements with ECPI.',
    focus:
      'Coursework focuses on foundational aide duties, patient communication, introductory medical terminology, clinical assistance, and essential rehabilitation support tasks without requiring a multi-year degree.',
    highlights: [
      'Expedited pathway into rehabilitation support',
      'Accelerated campus or online hybrid model',
      'Foundational clinical assistance and patient communication',
      'Introductory medical terminology',
      'Short-term private career training',
      'Open enrollment windows may be available',
    ],
  }),
  createProgram({
    name: 'Florida A&M University Online Physical Therapy Aide Training',
    location: 'Online, Tallahassee, FL',
    type: 'Online continuing education and physical therapy aide training',
    sourceUrl: 'https://www.famu.edu/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Florida+A%26M+University+Tallahassee+FL&output=embed',
    overview:
      'Florida A&M University offers online continuing education and professional development training for individuals interested in physical therapy aide work.',
    credential:
      'Continuing education credential for entry-level non-licensed clinical support',
    format: 'Flexible, self-paced web-based study',
    length:
      'Short-term continuing education schedule; confirm current course availability',
    tuition:
      'Fees are scaled for short-term continuing education units. Confirm current course pricing, materials, enrollment terms, and examination fees with Florida A&M University.',
    admissions:
      'Open online admissions are available. Review current enrollment requirements, course access, completion expectations, and credential details before registering.',
    focus:
      'The training covers basic patient positioning, clinic safety, therapeutic modalities, and medical office procedures for individuals seeking non-licensed physical therapy aide roles.',
    highlights: [
      'Flexible online continuing education format',
      'Self-paced web-based study',
      'Patient positioning and clinic safety fundamentals',
      'Therapeutic modalities and medical office procedures',
      'Short-term continuing education unit pricing',
      'Open online admissions',
    ],
  }),
  createProgram({
    name: 'University of Central Florida Physical Therapy Aide Continuing Education',
    location: 'Orlando, FL',
    type: 'University continuing education physical therapy aide course',
    sourceUrl: 'https://www.ce.ucf.edu/',
    mapEmbedUrl:
      'https://www.google.com/maps?q=University+of+Central+Florida+Orlando+FL&output=embed',
    overview:
      'UCF Continuing Education provides a local Orlando option for career-switchers seeking foundational physical therapy aide knowledge.',
    credential:
      'Continuing education training for physical therapy aide support',
    format: 'Flexible online continuing education course',
    length:
      'Course-based schedule; confirm the current offering and completion timeline',
    tuition:
      'Costs are billed on a per-course continuing education basis. Confirm current registration fees, materials, and payment terms with UCF Continuing Education.',
    admissions:
      'Open enrollment is available. Review the current course listing for technology requirements, schedule, delivery format, and completion expectations.',
    focus:
      'The curriculum introduces fundamental terminology, basic anatomy, kinesiology concepts, and daily clinical operations for non-licensed support roles in physical therapy clinics.',
    highlights: [
      'Local Orlando continuing education option',
      'Flexible online learning format',
      'Introduces anatomy, kinesiology, and clinical operations',
      'Per-course continuing education pricing',
      'Open enrollment',
      'Designed for career-switchers seeking healthcare exposure',
    ],
  }),
  createProgram({
    name: 'Seminole State College Certified Physical Therapy Aide Continuing Education',
    location: 'Altamonte Springs, FL',
    type: 'Continuing education physical therapy aide program',
    sourceUrl: 'https://www.seminolestate.edu/ce',
    mapEmbedUrl:
      'https://www.google.com/maps?q=Seminole+State+College+Altamonte+Springs+Campus+FL&output=embed',
    overview:
      'Seminole State College offers continuing education training for people seeking a focused pathway into physical therapy aide positions.',
    credential:
      'Physical therapy aide training with certification voucher option',
    format:
      'Self-paced digital instruction with integrated certification preparation',
    length:
      'Short-term continuing education schedule; confirm current course dates',
    tuition:
      'Tuition and material fees cover the comprehensive training modules and associated certification voucher. Confirm current pricing and voucher terms with Seminole State College.',
    admissions:
      'A high school diploma or equivalent is required for enrollment. Confirm current registration, technology, completion, and certification eligibility requirements.',
    focus:
      'The program teaches fundamental clinical support, legal and ethical guidelines, basic rehabilitation protocols, and workplace skills for physical therapy aide positions.',
    highlights: [
      'Continuing education option through Seminole State',
      'Targeted online instruction',
      'Self-paced digital learning format',
      'Integrated certification voucher',
      'Clinical support, ethics, and rehabilitation protocol instruction',
      'High school diploma or equivalent required',
    ],
  }),
];

export const resources = [
  {
    title: 'American Physical Therapy Association',
    description:
      'Explore physical therapy education, professional standards, advocacy, and career resources.',
    url: 'https://www.apta.org/',
  },
  {
    title: 'Federation of State Boards of Physical Therapy',
    description:
      'Review physical therapist assistant licensure and National Physical Therapy Examination resources.',
    url: 'https://www.fsbpt.org/',
  },
  {
    title: 'Florida Board of Physical Therapy',
    description:
      'Find Florida licensing, renewal, rules, and practice information for physical therapists and physical therapist assistants.',
    url: 'https://floridasphysicaltherapy.gov/',
  },
];